/**
 * Phase-0 rest-space damage-field spike.
 *
 * This is deliberately a CPU reference implementation: it defines the
 * storage contract, sampling coordinates, and tier memory without becoming a
 * Phase-1 visual system. The renderer decision chooses the eventual binding
 * location; all tiers use the same wall-local/rest-space coordinates.
 */

export type DamageFieldRendererDecision = 'tsl' | 'glsl';

export interface DamageFieldDimensions {
  readonly width: number;
  readonly height: number;
  readonly depth: number;
  /** RGBA8 stores damage, birth, seed, reserved. */
  readonly channels: 4;
}

export interface DamageFieldHalfExtents {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface RestSpacePoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface DamageStamp {
  readonly position: RestSpacePoint;
  readonly radius: number;
  readonly damage: number;
  /** Normalized birth time in the current clock window (0..1). */
  readonly age: number;
  /** Per-impact identity (0..1). First write wins on a texel. */
  readonly seed: number;
}

export interface DamageField {
  readonly dimensions: DamageFieldDimensions;
  readonly halfExtents: DamageFieldHalfExtents;
  readonly data: Uint8Array;
}

export const DAMAGE_FIELD_CANDIDATE: DamageFieldDimensions = Object.freeze({
  width: 96,
  height: 64,
  depth: 12,
  channels: 4,
});

export const DAMAGE_FIELD_TIER_FORMATS: Readonly<Record<
  'off' | 'basic' | 'high',
  DamageFieldDimensions | null
>> = Object.freeze({
  off: null,
  basic: DAMAGE_FIELD_CANDIDATE,
  high: Object.freeze({ width: 192, height: 128, depth: 24, channels: 4 }),
});

/** `96 × 64 × 12 × 4` bytes = 294,912 bytes = 288 KiB. */
export function damageFieldMemoryBytes(dimensions: DamageFieldDimensions): number {
  return dimensions.width * dimensions.height * dimensions.depth * dimensions.channels;
}

export function damageFieldMemoryKiB(dimensions: DamageFieldDimensions): number {
  return damageFieldMemoryBytes(dimensions) / 1024;
}

export function createDamageField(
  halfExtents: DamageFieldHalfExtents,
  dimensions: DamageFieldDimensions = DAMAGE_FIELD_CANDIDATE,
): DamageField {
  if (
    !Number.isInteger(dimensions.width) ||
    !Number.isInteger(dimensions.height) ||
    !Number.isInteger(dimensions.depth) ||
    dimensions.width < 1 ||
    dimensions.height < 1 ||
    dimensions.depth < 1 ||
    dimensions.channels !== 4
  ) {
    throw new RangeError('Damage-field dimensions must be positive integer RGBA8 dimensions');
  }
  return {
    dimensions,
    halfExtents,
    data: new Uint8Array(damageFieldMemoryBytes(dimensions)),
  };
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Map a wall-local `surfaceRestPosition` into normalized texture space. */
export function restPositionToNormalized(
  position: RestSpacePoint,
  halfExtents: DamageFieldHalfExtents,
): RestSpacePoint {
  return {
    x: clamp01(position.x / (2 * halfExtents.x) + 0.5),
    y: clamp01(position.y / (2 * halfExtents.y) + 0.5),
    z: clamp01(position.z / (2 * halfExtents.z) + 0.5),
  };
}

export function restPositionToTexel(
  position: RestSpacePoint,
  halfExtents: DamageFieldHalfExtents,
  dimensions: DamageFieldDimensions = DAMAGE_FIELD_CANDIDATE,
): { x: number; y: number; z: number } {
  const normalized = restPositionToNormalized(position, halfExtents);
  return {
    x: Math.min(dimensions.width - 1, Math.floor(normalized.x * dimensions.width)),
    y: Math.min(dimensions.height - 1, Math.floor(normalized.y * dimensions.height)),
    z: Math.min(dimensions.depth - 1, Math.floor(normalized.z * dimensions.depth)),
  };
}

function fieldIndex(field: DamageField, x: number, y: number, z: number): number {
  return ((z * field.dimensions.height + y) * field.dimensions.width + x)
    * field.dimensions.channels;
}

/** Stamp max damage and max birth into the bounded RGBA8 field. Seed is first-write-wins. */
export function stampDamageField(field: DamageField, stamp: DamageStamp): number {
  const { width, height, depth } = field.dimensions;
  const radius = Math.max(0, stamp.radius);
  const radiusSq = radius * radius;
  const center = restPositionToNormalized(stamp.position, field.halfExtents);
  const sx = Math.max(0, Math.floor((center.x - radius / (2 * field.halfExtents.x)) * width));
  const ex = Math.min(width - 1, Math.ceil((center.x + radius / (2 * field.halfExtents.x)) * width));
  const sy = Math.max(0, Math.floor((center.y - radius / (2 * field.halfExtents.y)) * height));
  const ey = Math.min(height - 1, Math.ceil((center.y + radius / (2 * field.halfExtents.y)) * height));
  const sz = Math.max(0, Math.floor((center.z - radius / (2 * field.halfExtents.z)) * depth));
  const ez = Math.min(depth - 1, Math.ceil((center.z + radius / (2 * field.halfExtents.z)) * depth));
  const damage = Math.round(clamp01(stamp.damage) * 255);
  const age = Math.round(clamp01(stamp.age) * 255);
  const seed = Math.round(clamp01(stamp.seed) * 255);
  let touched = 0;

  for (let z = sz; z <= ez; z += 1) {
    const pz = ((z + 0.5) / depth - 0.5) * 2 * field.halfExtents.z;
    for (let y = sy; y <= ey; y += 1) {
      const py = ((y + 0.5) / height - 0.5) * 2 * field.halfExtents.y;
      for (let x = sx; x <= ex; x += 1) {
        const px = ((x + 0.5) / width - 0.5) * 2 * field.halfExtents.x;
        const dx = px - stamp.position.x;
        const dy = py - stamp.position.y;
        const dz = pz - stamp.position.z;
        if (dx * dx + dy * dy + dz * dz > radiusSq) continue;
        const offset = fieldIndex(field, x, y, z);
        const previousDamage = field.data[offset];
        if (damage > previousDamage) field.data[offset] = damage;
        if (age > field.data[offset + 1]) field.data[offset + 1] = age;
        // First-write-wins: re-hits must not re-roll an existing crack identity.
        if (previousDamage === 0 && damage > 0) field.data[offset + 2] = seed;
        // A is reserved and stays 0.
        touched += 1;
      }
    }
  }
  return touched;
}

export function sampleDamageField(field: DamageField, position: RestSpacePoint): {
  damage: number;
  age: number;
  seed: number;
} {
  const texel = restPositionToTexel(position, field.halfExtents, field.dimensions);
  const offset = fieldIndex(field, texel.x, texel.y, texel.z);
  return {
    damage: field.data[offset] / 255,
    age: field.data[offset + 1] / 255,
    seed: field.data[offset + 2] / 255,
  };
}

/** The rest-position varying is the stable binding seam for either renderer. */
export function damageFieldBindingLocation(
  decision: DamageFieldRendererDecision,
): 'tsl-attribute-surfaceRestPosition' | 'glsl-varying-surfaceRestPosition' {
  return decision === 'tsl'
    ? 'tsl-attribute-surfaceRestPosition'
    : 'glsl-varying-surfaceRestPosition';
}
